import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supportAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface FormState {
  subject: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High';
}

interface TicketRow {
  id: number;
  jira_issue_key: string;
  subject: string | null;
  priority: string | null;
  status: string | null;
  updated_at: string | null;
  created_at: string;
  source: 'pmp_submit' | 'admin_bind';
  user?: {
    id: number;
    name: string;
    email: string;
  } | null;
}

interface TicketComment {
  id: string;
  author: string;
  body: string;
  created_at: string;
  source: string;
}

interface TicketDetail extends TicketRow {
  browse_url: string;
  comments: TicketComment[];
}

interface UserOption {
  id: number;
  name: string;
  email: string;
}

interface JiraWebhookConfig {
  secret_configured: boolean;
  webhook_url: string | null;
  events: string[];
  jql_filter: string | null;
  jira_steps: string[];
}

const statusColor = (status: string | null): 'default' | 'info' | 'success' | 'warning' => {
  if (!status) return 'default';
  const s = status.toLowerCase();
  if (s.includes('done') || s.includes('closed') || s.includes('resolved')) return 'success';
  if (s.includes('progress') || s.includes('review')) return 'info';
  if (s.includes('todo') || s.includes('open') || s.includes('backlog')) return 'warning';
  return 'default';
};

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const HelpSupport: React.FC = () => {
  const { user } = useAuth();
  const isPmpAdmin = user?.roles?.some((role: any) => role.slug === 'pmp_admin') ?? false;

  const [tab, setTab] = useState(0);

  // --- Submit form state ---
  const [form, setForm] = useState<FormState>({ subject: '', description: '', priority: 'Medium' });
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketKey, setTicketKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- My tickets state ---
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);

  // --- All tickets (admin) state ---
  const [allTickets, setAllTickets] = useState<TicketRow[]>([]);
  const [allTicketsLoading, setAllTicketsLoading] = useState(false);
  const [allTicketsError, setAllTicketsError] = useState<string | null>(null);

  // --- Detail dialog state ---
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // --- Admin bind state ---
  const [bindUser, setBindUser] = useState<UserOption | null>(null);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [bindKey, setBindKey] = useState('');
  const [binding, setBinding] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  const [bindSuccess, setBindSuccess] = useState<string | null>(null);

  // --- JIRA webhook setup (admin) ---
  const [webhookConfig, setWebhookConfig] = useState<JiraWebhookConfig | null>(null);
  const [webhookConfigLoading, setWebhookConfigLoading] = useState(false);
  const [webhookCopyMsg, setWebhookCopyMsg] = useState<string | null>(null);

  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    setTicketsError(null);
    try {
      const res = await supportAPI.listTickets();
      setTickets(res.data.data ?? []);
      if (res.data.jira_error) {
        setTicketsError('Live status from JIRA is currently unavailable. Showing saved details.');
      }
    } catch {
      setTicketsError('Failed to load your tickets. Please try again.');
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  const loadAllTickets = useCallback(async () => {
    setAllTicketsLoading(true);
    setAllTicketsError(null);
    try {
      const res = await supportAPI.listAllTickets();
      setAllTickets(res.data.data ?? []);
      if (res.data.jira_error) {
        setAllTicketsError('Live status from JIRA is currently unavailable. Showing saved details.');
      }
    } catch {
      setAllTicketsError('Failed to load all tickets. Please try again.');
    } finally {
      setAllTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (isPmpAdmin && tab === 2) {
      loadAllTickets();
    }
  }, [isPmpAdmin, tab, loadAllTickets]);

  const loadWebhookConfig = useCallback(async () => {
    setWebhookConfigLoading(true);
    try {
      const res = await supportAPI.getJiraWebhookConfig();
      setWebhookConfig(res.data.data ?? null);
    } catch {
      setWebhookConfig(null);
    } finally {
      setWebhookConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPmpAdmin && tab === 4) {
      loadWebhookConfig();
    }
  }, [isPmpAdmin, tab, loadWebhookConfig]);

  const copyWebhookUrl = async () => {
    if (!webhookConfig?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(webhookConfig.webhook_url);
      setWebhookCopyMsg('Webhook URL copied.');
    } catch {
      setWebhookCopyMsg('Could not copy — select and copy the URL manually.');
    }
  };

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScreenshot(e.target.files?.[0] ?? null);
  };

  const handleRemoveFile = () => {
    setScreenshot(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTicketKey(null);

    if (!form.subject.trim()) {
      setError('Subject is required.');
      return;
    }
    if (!form.description.trim()) {
      setError('Description is required.');
      return;
    }

    const data = new FormData();
    data.append('subject', form.subject.trim());
    data.append('description', form.description.trim());
    data.append('priority', form.priority);
    if (screenshot) data.append('screenshot', screenshot);

    setSubmitting(true);
    try {
      const res = await supportAPI.submitTicket(data);
      setTicketKey(res.data.ticket_key);
      setForm({ subject: '', description: '', priority: 'Medium' });
      setScreenshot(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadTickets();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (ticketId: number) => {
    setDetail(null);
    setDetailLoading(true);
    setCommentBody('');
    setCommentError(null);
    try {
      const res = await supportAPI.getTicket(ticketId);
      setDetail(res.data.data);
    } catch {
      setCommentError('Failed to load ticket details.');
      setDetail({ id: ticketId } as TicketDetail);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setCommentError(null);
    setCommentBody('');
  };

  const handleAddComment = async () => {
    if (!detail || !commentBody.trim()) return;
    setCommentSubmitting(true);
    setCommentError(null);
    try {
      const res = await supportAPI.addComment(detail.id, commentBody.trim());
      setDetail({ ...detail, comments: res.data.comments ?? detail.comments });
      setCommentBody('');
    } catch (err: any) {
      setCommentError(err.response?.data?.message || 'Failed to post comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const searchUsers = async (q: string) => {
    setUserSearchLoading(true);
    try {
      const res = await userAPI.list({ q, status: 'active' });
      setUserOptions(res.data.data ?? []);
    } catch {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleBind = async () => {
    setBindError(null);
    setBindSuccess(null);
    if (!bindUser) {
      setBindError('Please select a user.');
      return;
    }
    if (!bindKey.trim()) {
      setBindError('Please enter a JIRA issue key.');
      return;
    }
    setBinding(true);
    try {
      const res = await supportAPI.bindTicket(bindUser.id, bindKey.trim());
      setBindSuccess(res.data.message || 'Ticket linked successfully.');
      setBindUser(null);
      setBindKey('');
      loadTickets();
      if (isPmpAdmin) loadAllTickets();
    } catch (err: any) {
      setBindError(err.response?.data?.message || 'Failed to link ticket.');
    } finally {
      setBinding(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <HelpOutlineIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5">Help & Support</Typography>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Submit Request" />
        <Tab label="My Tickets" />
        {isPmpAdmin && <Tab label="All Tickets" />}
        {isPmpAdmin && <Tab label="Link Ticket" />}
        {isPmpAdmin && <Tab label="JIRA Webhook" />}
      </Tabs>

      {tab === 0 && (
        <Stack spacing={3} maxWidth={720}>
          {ticketKey && (
            <Alert severity="success" onClose={() => setTicketKey(null)}>
              Your support ticket <strong>{ticketKey}</strong> has been created successfully. You can
              track its progress under <strong>My Tickets</strong>.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Card>
            <CardHeader
              title="Submit a Support Request"
              subheader="Describe your issue and we'll create a ticket for our technical team."
            />
            <Divider />
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField
                    label="Subject"
                    value={form.subject}
                    onChange={(e) => handleChange('subject', e.target.value)}
                    required
                    fullWidth
                    placeholder="Brief summary of the issue"
                  />

                  <TextField
                    label="Description"
                    value={form.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    required
                    fullWidth
                    multiline
                    minRows={5}
                    placeholder="Please describe the issue in detail — steps to reproduce, expected vs actual behaviour, etc."
                  />

                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={form.priority}
                      label="Priority"
                      onChange={(e) => handleChange('priority', e.target.value as FormState['priority'])}
                    >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Screenshot (optional — jpg, png, gif, pdf, max 10 MB)
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    {screenshot ? (
                      <Chip
                        icon={<AttachFileIcon />}
                        label={screenshot.name}
                        onDelete={handleRemoveFile}
                        variant="outlined"
                      />
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Attach File
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" disabled={submitting} sx={{ minWidth: 160 }}>
                      {submitting ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={16} color="inherit" />
                          <span>Submitting...</span>
                        </Stack>
                      ) : (
                        'Submit Ticket'
                      )}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                What happens next?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once you submit, a JIRA ticket is created and assigned to our technical team. You can
                track status and add comments under <strong>My Tickets</strong>, and you will also
                receive updates via your registered email address. For urgent issues, please mark the
                priority as <strong>High</strong>.
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {tab === 1 && (
        <Card>
          <CardHeader
            title="My Tickets"
            action={
              <IconButton onClick={loadTickets} disabled={ticketsLoading} aria-label="Refresh">
                <RefreshIcon />
              </IconButton>
            }
          />
          <Divider />
          <CardContent>
            {ticketsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {ticketsError}
              </Alert>
            )}
            {ticketsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : tickets.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                You have no support tickets yet.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.map((t) => (
                      <TableRow
                        key={t.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openDetail(t.id)}
                      >
                        <TableCell>{t.jira_issue_key}</TableCell>
                        <TableCell>{t.subject || '—'}</TableCell>
                        <TableCell>
                          {t.status ? (
                            <Chip label={t.status} size="small" color={statusColor(t.status)} />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{t.priority || '—'}</TableCell>
                        <TableCell>{formatDate(t.updated_at)}</TableCell>
                        <TableCell>{formatDate(t.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 2 && isPmpAdmin && (
        <Card>
          <CardHeader
            title="All Mapped Tickets"
            subheader="Every JIRA ticket linked to a PMP user, with live status."
            action={
              <IconButton onClick={loadAllTickets} disabled={allTicketsLoading} aria-label="Refresh">
                <RefreshIcon />
              </IconButton>
            }
          />
          <Divider />
          <CardContent>
            {allTicketsError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {allTicketsError}
              </Alert>
            )}
            {allTicketsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : allTickets.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No tickets have been mapped to users yet.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allTickets.map((t) => (
                      <TableRow
                        key={t.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openDetail(t.id)}
                      >
                        <TableCell>{t.jira_issue_key}</TableCell>
                        <TableCell>{t.subject || '—'}</TableCell>
                        <TableCell>
                          {t.user ? (
                            <Box>
                              <Typography variant="body2">{t.user.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t.user.email}
                              </Typography>
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {t.status ? (
                            <Chip label={t.status} size="small" color={statusColor(t.status)} />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{t.priority || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={t.source === 'admin_bind' ? 'Admin linked' : 'User submitted'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(t.updated_at)}</TableCell>
                        <TableCell>{formatDate(t.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 3 && isPmpAdmin && (
        <Card sx={{ maxWidth: 640 }}>
          <CardHeader
            title="Link an Existing JIRA Ticket"
            subheader="Bind a JIRA issue to a user so they can track and comment on it."
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              {bindSuccess && (
                <Alert severity="success" onClose={() => setBindSuccess(null)}>
                  {bindSuccess}
                </Alert>
              )}
              {bindError && (
                <Alert severity="error" onClose={() => setBindError(null)}>
                  {bindError}
                </Alert>
              )}

              <Autocomplete
                options={userOptions}
                value={bindUser}
                loading={userSearchLoading}
                getOptionLabel={(o) => `${o.name} (${o.email})`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => setBindUser(v)}
                onInputChange={(_, v) => {
                  if (v && v.length >= 2) searchUsers(v);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="User" placeholder="Search by name or email" />
                )}
              />

              <TextField
                label="JIRA Issue Key"
                value={bindKey}
                onChange={(e) => setBindKey(e.target.value)}
                placeholder="e.g. PROJ-123"
                fullWidth
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleBind} disabled={binding} sx={{ minWidth: 140 }}>
                  {binding ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={16} color="inherit" />
                      <span>Linking...</span>
                    </Stack>
                  ) : (
                    'Link Ticket'
                  )}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {tab === 4 && isPmpAdmin && (
        <Card sx={{ maxWidth: 720 }}>
          <CardHeader
            title="JIRA Webhook Setup"
            subheader="Configure this in JIRA Cloud so mapped users get email when support comments or status changes."
          />
          <Divider />
          <CardContent>
            {webhookConfigLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2.5}>
                {!webhookConfig?.secret_configured && (
                  <Alert severity="warning">
                    <strong>JIRA_WEBHOOK_SECRET</strong> is not set in the server{' '}
                    <code>.env</code>. Generate one with{' '}
                    <code>openssl rand -hex 32</code>, add it to <code>.env</code>, then run{' '}
                    <code>php artisan config:clear</code>.
                  </Alert>
                )}

                {webhookCopyMsg && (
                  <Alert severity="info" onClose={() => setWebhookCopyMsg(null)}>
                    {webhookCopyMsg}
                  </Alert>
                )}

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Webhook URL (paste into JIRA)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      value={webhookConfig?.webhook_url ?? 'Set JIRA_WEBHOOK_SECRET first'}
                      fullWidth
                      multiline
                      minRows={2}
                      InputProps={{ readOnly: true }}
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={copyWebhookUrl}
                      disabled={!webhookConfig?.webhook_url}
                      sx={{ flexShrink: 0 }}
                    >
                      Copy
                    </Button>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Events to enable in JIRA
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(webhookConfig?.events ?? []).map((ev) => (
                      <Chip key={ev} label={ev} size="small" />
                    ))}
                  </Stack>
                </Box>

                {webhookConfig?.jql_filter && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommended JQL filter
                    </Typography>
                    <TextField
                      value={webhookConfig.jql_filter}
                      fullWidth
                      size="small"
                      InputProps={{ readOnly: true }}
                    />
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Steps in JIRA (site admin)
                  </Typography>
                  <Typography component="ol" variant="body2" color="text.secondary" sx={{ pl: 2.5, m: 0 }}>
                    {(webhookConfig?.jira_steps ?? []).map((step, i) => (
                      <li key={i} style={{ marginBottom: 8 }}>
                        {step}
                      </li>
                    ))}
                  </Typography>
                </Box>

                <Alert severity="info">
                  JIRA does not give you a webhook secret — you create{' '}
                  <strong>JIRA_WEBHOOK_SECRET</strong> in PMP and put the same value in the URL{' '}
                  <code>?token=...</code> when creating the webhook in JIRA.
                </Alert>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={detail !== null || detailLoading} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {detail?.jira_issue_key ? `Ticket ${detail.jira_issue_key}` : 'Ticket'}
          <IconButton
            onClick={closeDetail}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">{detail.subject || '—'}</Typography>
                {detail.user && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Mapped to {detail.user.name} ({detail.user.email})
                  </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                  {detail.status && (
                    <Chip label={detail.status} size="small" color={statusColor(detail.status)} />
                  )}
                  {detail.priority && <Chip label={detail.priority} size="small" variant="outlined" />}
                  {detail.browse_url && (
                    <Link href={detail.browse_url} target="_blank" rel="noopener" variant="body2">
                      Open in JIRA
                    </Link>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Typography variant="subtitle2">Comments</Typography>
              {detail.comments && detail.comments.length > 0 ? (
                <Stack spacing={1.5}>
                    {detail.comments.map((c) => (
                    <Box key={c.id} sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {c.author}
                          </Typography>
                          <Chip
                            label={c.source === 'pmp' ? 'Customer' : 'Support'}
                            size="small"
                            color={c.source === 'pmp' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(c.created_at)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {c.body}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No comments yet.
                </Typography>
              )}

              <Divider />

              {commentError && <Alert severity="error">{commentError}</Alert>}

              <TextField
                label="Add a comment"
                value={commentBody}
                onChange={(e) => setCommentBody(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={commentSubmitting || !commentBody.trim()}
                >
                  {commentSubmitting ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={16} color="inherit" />
                      <span>Posting...</span>
                    </Stack>
                  ) : (
                    'Post Comment'
                  )}
                </Button>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HelpSupport;
